import React from "react";

import {cn} from "@isudio/utils";
import {$store} from "@isudio/globals";
import type {IAssetSort} from "@isudio/data";
import {HeaderLeftNav, HeaderRightNav, type BreadcrumbItems} from "../";
import {useRef, useEffect} from "react";

interface HeaderProps {
  className?: string;
  position?: "sticky" | "fixed";
  visible?: boolean;
  title?: string;
  icon?: string;
  image?: string;
  showRightNav?: boolean;
  searchRef?: React.RefObject<HTMLInputElement>;
  onSortChange?: (sort: IAssetSort) => void;
  // onFilterChange?: (filter: IAssetFilter) => void;
  onFilterChange?: (payload: any) => void;
  selectedAssetType?: string | null;
  selectedSearchBy?: string | null;
  onSearchChange?: (search: string) => void;
  breadCrumbs?: BreadcrumbItems[];
  onHeightChange?: (height: number) => void;
}

const Header = React.forwardRef<Element, HeaderProps>(
  ({
     className = "page-header",
     position = "sticky",
     visible = false,
     title,
     icon,
     image,
     showRightNav = false,
     searchRef,
     onSortChange,
     onFilterChange,
     selectedAssetType,
     selectedSearchBy,
     onSearchChange,
     breadCrumbs,
     onHeightChange
   }: HeaderProps
  ) => {
    const headerRef = useRef<HTMLDivElement>(null);

    const onMenuClick = (e: any) => {
      const rect = (e.target as HTMLElement).getBoundingClientRect();

      $store.App.mousePosition$.next({
        type: "mouse",
        x: rect.right - 10,
        y: rect.bottom + 10,
      });

      $store.App.showMenu$.next(true);
      $store.App.menuClicked$.next(false);
    }

    useEffect(() => {
      const resizeObserver = new ResizeObserver(() => {
        if (headerRef.current) {
          onHeightChange?.(headerRef.current.clientHeight);
        }
      });

      if (headerRef.current) {
        resizeObserver.observe(headerRef.current);
      }

      return () => resizeObserver.disconnect();
    }, [onHeightChange]);

    return (
      <header
        ref={headerRef}
        className={cn(
          `${className}`, `header w-full top-0 p-4 z-40 border-none ${position} bg-linear-to-b`,
          `${position === "fixed" ? "from-gray-darker/95 to-gray-medium/60" : "from-gray-darker to-gray-medium"}`,
          !visible
            ? "-translate-y-full opacity-0 pointer-events-none"
            : "translate-y-0 opacity-100"
        )}>
        <div className="flex items-center w-full gap-2 md:gap-4">
          <div
            className={cn(
              "shrink-0 flex items-center justify-center cursor-default"
            )}>
            <img
              src={"/assets/img/port/logo-white.png"}
              alt={`isudio.com`}
              className="hidden lg:block w-auto h-12 object-cover border-none"
            />
            <img
              src={"/assets/icons/icon-128x128.png"}
              alt={`isudio.com`}
              className="block lg:hidden w-auto h-12 object-cover border-none"
            />
          </div>

          <div className="flex flex-col flex-1 gap-2 text-left">
            <div
              className="container-fluid flex justify-between items-center">
              <div>
                <HeaderLeftNav
                  title={title}
                  icon={icon}
                  image={image}
                  breadCrumbs={breadCrumbs}
                />
              </div>
              {showRightNav ?
                <div>
                  <HeaderRightNav
                    searchRef={searchRef}
                    onSortChange={onSortChange}
                    onFilterChange={onFilterChange}
                    selectedAssetType={selectedAssetType}
                    selectedSearchBy={selectedSearchBy}
                    onSearchChange={onSearchChange}
                  />
                </div>
                : null
              }
            </div>
          </div>

          <div
            className={cn(
              "w-7 flex items-center justify-center cursor-default"
            )}>
            <i
              className="fa-duotone fa-light fa-list-dropdown text-3xl text-pale-white cursor-pointer transition duration-200 hover:text-generic-hover hover:scale-105"
              onClick={onMenuClick}/>
          </div>
        </div>
      </header>
    )
  }
);

Header.displayName = "Header";
export {Header};